import IRecorder from "./IRecorder";
import {mergeAudioBuffers} from "./soundUtilities";

export default class AudioWorkletRecorder implements IRecorder {
  #source: MediaStreamAudioSourceNode | null = null;
  #copyProcessor: AudioWorkletNode | null = null;
  #outputBuffer: AudioBuffer | null = null;
  #audioContext: AudioContext;
  #mediaStream: MediaStream | null = null;
  #isInitialized: boolean = false;

  initialize(audioContext: AudioContext): Promise<void> {
    return new Promise<void>((resolve, _reject) => {
      this.#audioContext = audioContext;

      this.#loadCopyProcessor()
        .then(() => {
          this.#isInitialized = true;
          resolve();
        });
    });
  }

  #ensureRecorderInitialized(): void {
    if (!this.#isInitialized) {
      throw new Error("MediaRecorderRecorder is not initialized");
    }
  }

  async #loadCopyProcessor() : Promise<void> {
    return this.#audioContext.audioWorklet.addModule('/pitchDetection/worklets/audio-processor.js');
  }

  getAudioBuffer(): Promise<AudioBuffer> {
    this.#ensureRecorderInitialized();

    return new Promise<AudioBuffer>((resolve, reject) => {
      if (this.#outputBuffer === null) {
        reject("No audio data recorded");
      } else {
        resolve(this.#outputBuffer as AudioBuffer);
      }
    });
  }

  #reset(): void {
    this.#cleanupCopyProcessor();
    this.#cleanupSource();
  }

  #getCopyProcessor(): AudioWorkletNode {
    if (this.#copyProcessor === null) {
      this.#copyProcessor = new AudioWorkletNode(this.#audioContext, 'audio-processor');
      this.#copyProcessor.port.onmessage = this.#onMessageFromCopyProcessor.bind(this);
    }

    return this.#copyProcessor;
  }

  startListening(mediaStream: MediaStream): void {
    this.#ensureRecorderInitialized();

    this.#reset();

    console.log("Recording with AudioWorkletRecorder");
    this.#mediaStream = mediaStream;

    // Create a MediaStreamAudioSourceNode from the MediaStream
    this.#source = this.#audioContext.createMediaStreamSource(this.#mediaStream);
    const copyProcessor= this.#getCopyProcessor();

    // Connect the source to the processor
    if (this.#source) {
      this.#source.connect(copyProcessor);

      // Connect the copyProcessor to the speakers
      // globalCopyProcessor.connect(this.#audioContext.destination);
    }
  }

  #onMessageFromCopyProcessor(event: MessageEvent): void {
    // Access the audio data sent from the processor
    const audioData = event.data;

    // Create an AudioBuffer with 1 channel and the length of the audioData, at the sample rate of the AudioContext
    const audioBuffer = this.#audioContext.createBuffer(1, audioData.length, this.#audioContext.sampleRate);

    // Copy the audioData to the AudioBuffer
    audioBuffer.copyToChannel(audioData, 0, 0);

    // Add the data to the global buffer
    this.#outputBuffer = this.#outputBuffer === null ? audioBuffer : mergeAudioBuffers(this.#audioContext, this.#outputBuffer, audioBuffer);
  }

  stopListening(): Promise<void> {
    return new Promise<void>((resolve, _reject) => {
      this.#ensureRecorderInitialized();
      this.#reset();
      resolve();
    });
  }

  #cleanupCopyProcessor(): void {
    if (this.#copyProcessor) {
      // Send a message to the audio worklet to stop processing audio
      this.#copyProcessor.port.postMessage({type: 'togglePostMessage', enabled: false});
      this.#copyProcessor.disconnect();
      this.#copyProcessor = null;
    }
    console.log("\tglobalCopyProcessor disconnected");
  }

  #cleanupSource(): void {
    this.#source?.disconnect();
    this.#source = null;
    console.log("\tglobalSource disconnected");
  }

}